/**
 * Connection state types for the browser game shell.
 *
 * Represents the lifecycle states of the WebSocket connection to the
 * authoritative backend server.
 */

/**
 * The possible states of the WebSocket connection.
 */
export type ConnectionState =
  | 'disconnected'   // Not connected, no active connection attempt
  | 'connecting'     // Actively trying to establish a connection
  | 'connected'      // Connected and ready to send/receive messages
  | 'reconnecting'   // Connection was lost, attempting to reconnect
  | 'error';         // Connection failed with a recoverable error

/**
 * Event emitted when the connection state changes.
 */
export interface ConnectionStateChangedEvent {
  previousState: ConnectionState;
  currentState: ConnectionState;
  error?: string;
}

/**
 * Callback type for connection state change listeners.
 */
export type ConnectionStateChangeListener = (
  event: ConnectionStateChangedEvent
) => void;

/**
 * Valid transitions between connection states.
 * Used for validation in the ConnectionService.
 */
export const VALID_CONNECTION_TRANSITIONS: Record<ConnectionState, ConnectionState[]> = {
  disconnected: ['connecting'],
  connecting: ['connected', 'error', 'disconnected'],
  connected: ['disconnected', 'reconnecting'],
  reconnecting: ['connected', 'error', 'disconnected'],
  error: ['connecting', 'reconnecting', 'disconnected'],
};

/**
 * Returns true if the given state transition is valid.
 */
export function isValidTransition(from: ConnectionState, to: ConnectionState): boolean {
  const allowed = VALID_CONNECTION_TRANSITIONS[from];
  return allowed.includes(to);
}

/**
 * Returns true if the connection is in an active (usable) state.
 */
export function isConnectionActive(state: ConnectionState): boolean {
  return state === 'connected';
}
